// agents/src/pages/admin/AdminUsers.jsx
//
// Subpágina del panel admin: restablecer la contraseña de cualquier usuario.
// Montada bajo /admin/users dentro de AdminLayout (que ya aplica el guard isAdmin()).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAllUsers } from '../../services/userService';
import { resetUserPassword } from '../../services/adminUserService';

export default function AdminUsers() {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getAllUsers()
      .then((data) => setUsers(data.users || []))
      .catch((err) => {
        console.error('[AdminUsers] Error cargando usuarios:', err);
        setError(t('admin.users.error.load'));
      });
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!selectedUserId) {
      setError(t('admin.users.error.noUser'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('admin.users.error.passwordMatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('admin.users.error.passwordLength'));
      return;
    }

    try {
      setSubmitting(true);
      const { user } = await resetUserPassword(selectedUserId, password);
      setMessage(t('admin.users.success', { email: user?.email || '' }));
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      console.error('[AdminUsers] Error restableciendo contraseña:', err);
      setError(err.message || t('admin.users.error.reset'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        {t('admin.users.title')}
      </h2>

      {message && (
        <p className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
          {message}
        </p>
      )}
      {error && (
        <p className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 bg-white border border-gray-200 rounded p-4">
        <label className="flex flex-col text-sm text-gray-700 gap-1">
          {t('admin.users.selectUser')}
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            required
          >
            <option value="">{t('admin.users.selectPlaceholder')}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col text-sm text-gray-700 gap-1">
          {t('admin.users.newPassword')}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            autoComplete="new-password"
            required
          />
        </label>

        <label className="flex flex-col text-sm text-gray-700 gap-1">
          {t('admin.users.confirmPassword')}
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            autoComplete="new-password"
            required
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="self-start px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? t('common.loading') : t('admin.users.resetButton')}
        </button>
      </form>
    </div>
  );
}
